/**
 * Advanced basketball stats calculator.
 * All formulas are derived from existing raw stats — no new data needed.
 * Used by GameCenterEstadisticas, CanteraStatsTab for advanced metrics.
 */

/**
 * Compute all advanced stats from raw box score data.
 * @param {Object} raw - Raw stats (points, field_goals_made, field_goals_attempted, etc.)
 * @returns {Object} Advanced stats
 */
export function computeAdvancedStats(raw) {
  const {
    points = 0,
    minutes_played = 0,
    field_goals_made = 0,
    field_goals_attempted = 0,
    three_points_made = 0,
    three_points_attempted = 0,
    free_throws_made = 0,
    free_throws_attempted = 0,
    offensive_rebounds = 0,
    defensive_rebounds = 0,
    rebounds = 0,
    assists = 0,
    steals = 0,
    blocks = 0,
    turnovers = 0,
    fouls = 0,
  } = raw;

  const totalFGA = field_goals_attempted + three_points_attempted;
  const totalFGM = field_goals_made + three_points_made;
  const min = parseFloat(minutes_played) || 0;

  // True Shooting % = PTS / (2 * (FGA + 0.44 * FTA))
  const tsaDenominator = 2 * (totalFGA + 0.44 * free_throws_attempted);
  const ts_pct = tsaDenominator > 0 ? (points / tsaDenominator * 100) : null;

  // Effective FG% = (FGM + 0.5 * 3PM) / FGA
  const efg_pct = totalFGA > 0 ? ((totalFGM + 0.5 * three_points_made) / totalFGA * 100) : null;

  // FG% (2pts + 3pts combined)
  const fg_pct = totalFGA > 0 ? (totalFGM / totalFGA * 100) : null;

  // 2PT%
  const two_pt_pct = field_goals_attempted > 0 ? (field_goals_made / field_goals_attempted * 100) : null;

  // 3PT%
  const three_pt_pct = three_points_attempted > 0 ? (three_points_made / three_points_attempted * 100) : null;

  // FT%
  const ft_pct = free_throws_attempted > 0 ? (free_throws_made / free_throws_attempted * 100) : null;

  // Assist-to-Turnover ratio
  const ast_to_ratio = turnovers > 0 ? (assists / turnovers) : (assists > 0 ? Infinity : null);

  // Estimated possessions = FGA + 0.44 * FTA + TO - OREB
  const possessions = totalFGA + 0.44 * free_throws_attempted + turnovers - offensive_rebounds;

  // Per-36 minutes stats (NBA standard normalization)
  const per36Factor = min > 0 ? (36 / min) : 0;
  const per_36 = min > 0 ? {
    points: round1(points * per36Factor),
    rebounds: round1((rebounds || (offensive_rebounds + defensive_rebounds)) * per36Factor),
    assists: round1(assists * per36Factor),
    steals: round1(steals * per36Factor),
    blocks: round1(blocks * per36Factor),
    turnovers: round1(turnovers * per36Factor),
  } : null;

  // Points per possession (offensive efficiency approximation)
  const pts_per_possession = possessions > 0 ? round2(points / possessions) : null;

  // Usage rate approximation = (FGA + 0.44 * FTA + TO) / (minutes * team_possessions_per_min)
  // Simplified: just raw usage without team context
  const usage_raw = totalFGA + 0.44 * free_throws_attempted + turnovers;

  // Points per minute
  const pts_per_min = min > 0 ? round2(points / min) : null;

  // Rebound rate per minute
  const reb_per_min = min > 0 ? round2((rebounds || (offensive_rebounds + defensive_rebounds)) / min) : null;

  return {
    ts_pct: round1(ts_pct),
    efg_pct: round1(efg_pct),
    fg_pct: round1(fg_pct),
    two_pt_pct: round1(two_pt_pct),
    three_pt_pct: round1(three_pt_pct),
    ft_pct: round1(ft_pct),
    ast_to_ratio: round2(ast_to_ratio),
    possessions: round1(possessions),
    pts_per_possession,
    pts_per_min,
    reb_per_min,
    usage_raw: round1(usage_raw),
    per_36,
  };
}

/**
 * Compute season averages from an array of game stats.
 * @param {Array} games - Array of raw stats objects (one per game)
 * @returns {Object} Season averages + totals + advanced
 */
export function computeSeasonAverages(games) {
  if (!games || games.length === 0) return null;

  const n = games.length;
  const totals = {};
  const fields = [
    'minutes_played', 'points', 'rebounds', 'assists', 'steals', 'blocks',
    'turnovers', 'fouls', 'field_goals_made', 'field_goals_attempted',
    'three_points_made', 'three_points_attempted', 'free_throws_made',
    'free_throws_attempted', 'offensive_rebounds', 'defensive_rebounds',
    'evaluation', 'charges_taken', 'charges_drawn', 'deflections',
    'plus_minus', 'fouls_received', 'points_in_paint', 'fastbreak_points',
    'second_chance_points'
  ];

  fields.forEach(f => { totals[f] = 0; });

  games.forEach(g => {
    fields.forEach(f => {
      totals[f] += parseFloat(g[f]) || 0;
    });
  });

  const averages = {};
  fields.forEach(f => {
    averages[f] = round1(totals[f] / n);
  });

  const advanced = computeAdvancedStats(totals);

  return {
    games_played: n,
    totals,
    averages,
    advanced,
  };
}

function round1(val) {
  if (val === null || val === undefined || !isFinite(val)) return null;
  return Math.round(val * 10) / 10;
}

function round2(val) {
  if (val === null || val === undefined || !isFinite(val)) return null;
  return Math.round(val * 100) / 100;
}
